import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { activeLineAt, LyricTimingV1, tappedLineStartMs, timingCounts } from 'fym-shared/src/music24/lyricTiming';
import { TypedMemo } from '../../../../common/types';
import { MasterPlayerControls } from '../player/useMasterPlayer';
import { LyricResyncNoteView } from './LyricResyncNoteView';
import { LyricTimingHead } from './LyricTimingHead';
import { LyricResyncNote } from './resyncOutcome';
import { LyricTimingRows } from './LyricTimingRows';
import { LyricTimingKeyHints, LyricTimingLegend } from './LyricTimingKeyHints';
import { playStartSec } from './lyricTimingFormat';
import { lyricTimingMessages } from './lyricTimingMessages';
import { useLyricEditorKeys } from './useLyricEditorKeys';
import { Panel } from '../../../../music24/components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the lyric timing; each part uses its own slice of it */
import styles from './LyricTiming.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface LyricTimingEditorViewProps {
  timing: LyricTimingV1;
  player: MasterPlayerControls;
  /** False while the AAC copy of the master is not ready. */
  canPlay: boolean;
  isPage: boolean;
  /** The demo artist: the lines play, nothing changes. */
  readOnly?: boolean;
  isSaving: boolean;
  isResyncing: boolean;
  /** The result of the last Re-sync of this page: an error, or "Synced again". */
  resyncNote?: LyricResyncNote | null;
  onResync: () => void;
  setStart: (index: number, startMs: number | null) => boolean;
  nudge: (index: number, deltaMs: number) => boolean;
}

/**
 * The sync editor (design s8): the head, the sync bar while a sync runs (s8b), the lines, the keys
 * and the dots. ▶ plays the master from 0.5 s before a line; the line that plays has the tint.
 * − / + and [ / ] move a start by 0.1 s (Shift 0.5 s); T sets the selected line to the play head
 * (the exact position at the key press, minus `LYRIC_TAP_REACTION_MS`). The tint leads the stored
 * start by `LYRIC_LINE_LEAD_SECONDS`, like the lyric videos.
 * While a sync runs, the lines show with no − and + (the times come in about a minute).
 */
const LyricTimingEditorViewComponent: React.FC<LyricTimingEditorViewProps> = ({
  timing,
  player,
  canPlay,
  isPage,
  readOnly = false,
  isSaving,
  isResyncing,
  resyncNote = null,
  onResync,
  setStart,
  nudge,
}) => {
  const intl = useIntl();
  const [selected, setSelected] = useState<number | null>(null);
  const isSyncing = timing.status === 'syncing';
  const canEdit = !isSyncing && !readOnly;
  const isPlaying = player.status === 'playing';
  const hasPosition = player.status === 'playing' || player.status === 'paused';
  const editedCount = useMemo(() => timingCounts(timing).editedCount, [timing]);

  // The display lead: the tint comes slightly before the stored start (fym-shared `activeLineAt`).
  const nowIndex = useMemo(
    () => (hasPosition ? (activeLineAt(timing, player.positionSec)?.index ?? null) : null),
    [hasPosition, player.positionSec, timing]
  );

  const { playFrom, toggle, livePositionSec } = player;
  const play = useCallback(
    (index: number) => {
      setSelected(index);
      const line = timing.lines[index];
      const at = line === undefined ? null : playStartSec(line);
      if (at !== null && canPlay) {
        playFrom(at);
      }
    },
    [canPlay, playFrom, timing.lines]
  );
  const onNudge = useCallback(
    (index: number, deltaMs: number) => {
      setSelected(index);
      nudge(index, deltaMs);
    },
    [nudge]
  );
  const togglePlay = useCallback(() => {
    if (canPlay) {
      toggle();
    }
  }, [canPlay, toggle]);
  const setToPlayhead = useCallback(
    (index: number) => {
      const at = hasPosition ? livePositionSec() : null;
      if (at !== null) {
        setStart(index, tappedLineStartMs(at * 1000));
      }
    },
    [hasPosition, livePositionSec, setStart]
  );

  const onKeyDown = useLyricEditorKeys({
    lineCount: timing.lines.length,
    selected,
    select: setSelected,
    togglePlay,
    canEdit,
    nudge: onNudge,
    setToPlayhead,
  });

  return (
    <div className="d-flex flex-column gap-2">
      <Panel className={styles.sync} data-testid="lyric-timing-editor">
        <LyricTimingHead
          timing={timing}
          isSaving={isSaving}
          isResyncing={isResyncing}
          onResync={readOnly ? undefined : onResync}
        />
        {resyncNote !== null && (
          <div className={styles.stateBody}>
            <LyricResyncNoteView note={resyncNote} />
          </div>
        )}
        {isSyncing && (
          <div className={styles.stateBody}>
            <span>
              {editedCount > 0
                ? intl.formatMessage(lyricTimingMessages.lyricsChanged, { count: editedCount })
                : intl.formatMessage(lyricTimingMessages.syncing)}
            </span>
            <span className={styles.bar}>
              <i />
            </span>
            <span className={styles.small}>{intl.formatMessage(lyricTimingMessages.syncingLines)}</span>
          </div>
        )}
        {timing.status === 'failed' && !readOnly && (
          <div className={styles.stateBody}>
            <span>{intl.formatMessage(lyricTimingMessages.byHandHint)}</span>
          </div>
        )}
        <LyricTimingRows
          lines={timing.lines}
          nowIndex={nowIndex}
          isPlaying={isPlaying}
          selected={selected}
          canEdit={canEdit}
          canPlay={canPlay}
          isPage={isPage}
          onPlay={play}
          onSelect={setSelected}
          onNudge={onNudge}
          onKeyDown={onKeyDown}
        />
        {canEdit && <LyricTimingKeyHints />}
      </Panel>
      {canEdit && <LyricTimingLegend />}
    </div>
  );
};

export const LyricTimingEditorView = TypedMemo(LyricTimingEditorViewComponent);
